import { WithChildren } from '@k3/core'
import {
  IContext,
  IUpdateContext,
} from '../utility/interface/terraceCover.interface'
import { cover } from '../utility/constant/helper.constant'
import {
  useStateLedSpots,
  useStateMarquee,
  useStateMeasure,
  useStateRoof,
  useStateSidewall,
  useStateTyp,
  useStateEgo,
  useStateNight,
} from '../hooks/state'
import {
  TAttachment,
  TDitchForm,
  TDockTile,
  TEnginePosition,
  TEngineType,
  TFabricColor,
  TGlassDensity,
  TLedSpots,
  TLedSpotsSet,
  TFacadae,
  TMarqueeType,
  TSidewallFront,
  TSidewallLeft,
  TSidewallRight,
  TSupportForm,
  TTerraceColor,
  TTerraceDepth,
  TTerraceModel,
  TTerraceWidth,
  TTyp,
  TPoles,
} from '../utility/type/terraceCover.type'

import { createContext, useContext } from 'react'
import { useStateEnvironment } from '../hooks/state/environment.hook'

export const MontageContext = createContext<IContext>({
  measure: {
    userModel: cover.terraceCover.model,
    userWidth: cover.terraceCover.width,
    userDepth: cover.terraceCover.depth,
    userHeight: cover.terraceCover.height,
    userAngle: cover.bar.angle,
    userPoles: cover.terraceCover.poles,
    windowsTopTotal: cover.terraceCover.windowsTopTotal,
    windowsTopWidth: cover.terraceCover.windowsTopWidth(),
    logsTopTotal: cover.terraceCover.logsTopTotal(),
    windowsFrontTotal: cover.terraceCover.windowsFrontTotal,
    windowsFrontWidth: cover.terraceCover.windowsFrontWidth(),
    logsFrontTotal: cover.terraceCover.logsFrontTotal(),
    windowsSideTotal: cover.terraceCover.windowsSideTotal,
    windowsSideWidth: cover.terraceCover.windowsSideWidth(),
    logsSideTotal: cover.terraceCover.logsSideTotal(),
    frontHeight: cover.terraceCover.frontHeight(),
    angleCompensation: cover.terraceCover.angleCompensation(),
    terraceDepth: cover.terrace.depth,
  },

  roof: {
    attachment: cover.terraceCover.attachment,
    userColor: cover.bar.color,
    ditchForm: cover.terraceCover.ditchForm,
    supportForm: cover.terraceCover.supportForm,
    safetyGlass: cover.terraceCover.safetyGlass,
    dockGlass: cover.terraceCover.dockGlass,
  },

  marquee: {
    type: cover.terraceCover.marquee,
    engine: cover.terraceCover.engine,
    enginePosition: cover.terraceCover.enginePosition,
    fabric: cover.terraceCover.fabricColor,
  },

  sidewall: {
    sidewallRight: cover.terraceCover.sidewallRight,
    sidewallLeft: cover.terraceCover.sidewallLeft,
    sidewallFront: cover.terraceCover.sidewallFront,
  },

  ledSpots: {
    type: cover.terraceCover.ledSpots,
    quantity: cover.terraceCover.ledSpotsSet,
  },

  typ: {
    typ: cover.terraceCover.typ,
    width: 0,
    depth: 0,
  },

  ego: {
    ego: cover.ego
  },

  night: {
    night: cover.night
  },
  
  environment: {
    facade: cover.environment.facade
  }
})

export const MontageUpdateContext = createContext<IUpdateContext>({
  onHandleModel: (value: TTerraceModel) => {},
  onHandleHeight: (value: number) => {},
  onHandleWidth: (value: TTerraceWidth) => {},
  onHandleDepth: (value: TTerraceDepth) => {},
  onHandleAngle: (value: number) => {},
  onHandleAttachment: (value: TAttachment) => {},
  onHandlePoles: (value: TPoles) => {},
  onHandleTyp: (value: TTyp) => {},
  onHandleDepthAndWidth: (width: any, depth: any) => {},
  onHandleTerraceColor: (value: TTerraceColor) => {},
  onHandleDitchForm: (value: TDitchForm) => {},
  onHandleSupportForm: (value: TSupportForm) => {},
  onHandleSafetyGlass: (value: TGlassDensity) => {},
  onHandleDockTile: (value: TDockTile) => {},
  onHandleMarquee: (value: TMarqueeType) => {},
  onHandleEngine: (value: TEngineType) => {},
  onHandleEnginePosition: (value: TEnginePosition) => {},
  onHandleFabric: (value: TFabricColor) => {},
  onHandleSidewallRight: (value: TSidewallRight) => {},
  onHandleSidewallLeft: (value: TSidewallLeft) => {},
  onHandleSidewallFront: (value: TSidewallFront) => {},
  onHandleLedSpots: (value: TLedSpots) => {}, 
  onHandleLedSpotsSet: (value: TLedSpotsSet) => {},
  onHandleEgo: (value: boolean) => {},
  onHandleNight: (value: boolean) => {},
  onHandleFacade: (value: TFacadae) => {},
})

// Provider
export const MontageProvider: React.FC<WithChildren> = ({ children }) => {
  const { measure, measureMethod } = useStateMeasure()
  const { roof, roofMethod } = useStateRoof()
  const { marquee, marqueeMethod } = useStateMarquee()
  const { sidewall, sidewallMethod } = useStateSidewall()
  const { ledSpots, ledSpotsMethod } = useStateLedSpots()
  const { typ, typMethod } = useStateTyp()
  const {ego, egoMethod } = useStateEgo()
  const { night, nightMethod} = useStateNight()
  const {environment, environmentMethod} = useStateEnvironment()

  return (
    <MontageUpdateContext.Provider
      value={{
        onHandleAngle: measureMethod.onHandleAngle,
        onHandleDepth: measureMethod.onHandleDepth,
        onHandleHeight: measureMethod.onHandleHeight,
        onHandleModel: measureMethod.onHandleModel,
        onHandleWidth: measureMethod.onHandleWidth,
        onHandleAttachment: roofMethod.onHandleAttachment,
        onHandlePoles: measureMethod.onHandlePoles,
        onHandleTyp: typMethod.onHandleTyp,
        onHandleDepthAndWidth: typMethod.onHandleDepthAndWidth,
        onHandleTerraceColor: roofMethod.onHandleTerraceColor,
        onHandleDitchForm: roofMethod.onHandleDitchForm,
        onHandleSupportForm: roofMethod.onHandleSupportForm,
        onHandleSafetyGlass: roofMethod.onHandleSafetyGlass,
        onHandleDockTile: roofMethod.onHandleDockTile,
        onHandleMarquee: marqueeMethod.onHandleMarquee,
        onHandleEngine: marqueeMethod.onHandleEngine,
        onHandleEnginePosition: marqueeMethod.onHandleEnginePosition,
        onHandleFabric: marqueeMethod.onHandleFabric,
        onHandleSidewallRight: sidewallMethod.onHandleSidewallRight,
        onHandleSidewallLeft: sidewallMethod.onHandleSidewallLeft,
        onHandleSidewallFront: sidewallMethod.onHandleSidewallFront,
        onHandleLedSpots: ledSpotsMethod.onHandleLedSpots,
        onHandleLedSpotsSet: ledSpotsMethod.onHandleLedSpotsSet,
        onHandleEgo: egoMethod.onHandleEgo,
        onHandleNight: nightMethod.onHandleNight,
        onHandleFacade: environmentMethod.onHandleFacade,
      }}
    >
      <MontageContext.Provider
        value={{
          measure,
          roof,
          marquee,
          sidewall,
          ledSpots,
          typ,
          ego,
          night,
          environment,

        }}
      >
        {children}
      </MontageContext.Provider>
    </MontageUpdateContext.Provider>
  )
}

export const useMontage = () => {
  return useContext(MontageContext)
}

export const useMontageUpdate = () => {
  return useContext(MontageUpdateContext)
}
